(function () {
  const CONFIG = window.CorgiWishlistConfig || {};
  const STORAGE_KEY = 'corgi:wishlist:v1';
  const SNAPSHOT_KEY = 'corgi:wishlist:products:v1';
  const CUSTOMER_ACCOUNT_AUTH_KEY = 'corgi:wishlist:customer-account:v1';
  const CUSTOMER_ACCOUNT_PKCE_KEY = 'corgi:wishlist:customer-account-pkce:v1';
  const CUSTOMER_ACCOUNT_SILENT_KEY = 'corgi:wishlist:customer-account-silent:v1';
  const PRODUCT_GID_PREFIX = 'gid://shopify/Product/';
  const WISHLIST_NAMESPACE = 'sidekick';
  const WISHLIST_KEY = 'wishlist';

  const state = {
    ids: [],
    snapshots: new Map(),
    productCache: new Map(),
    customerAccountEndpoint: '',
    customerAccountToken: '',
    customerAccountRefreshToken: '',
    customerAccountExpiresAt: 0,
    customerAccountReturnTo: '',
    customerId: '',
    serverLoaded: false,
    serverLoggedIn: Boolean(CONFIG.customerLoggedIn),
    syncing: false,
    ready: false,
  };

  function label(key) {
    return (CONFIG.labels && CONFIG.labels[key]) || '';
  }

  function normalizeProductGid(value) {
    const raw = String(value || '').trim();
    if (!raw) return '';
    if (/^gid:\/\/shopify\/Product\/\d+$/i.test(raw)) return raw;
    const numeric = raw.match(/\d+/)?.[0] || '';
    return numeric ? PRODUCT_GID_PREFIX + numeric : '';
  }

  function productIdFromGid(gid) {
    return String(gid || '').split('/').pop() || '';
  }

  function variantIdFromGid(gid) {
    return String(gid || '').split('/').pop() || '';
  }

  function cleanText(value) {
    return String(value || '').replace(/\s+/g, ' ').trim();
  }

  function dedupe(ids) {
    const seen = new Set();
    const list = [];
    ids.forEach((id) => {
      const gid = normalizeProductGid(id);
      if (!gid || seen.has(gid)) return;
      seen.add(gid);
      list.push(gid);
    });
    return list;
  }

  function readLocal() {
    try {
      const parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]');
      return Array.isArray(parsed) ? dedupe(parsed) : [];
    } catch (_error) {
      return [];
    }
  }

  function writeLocal(ids) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(dedupe(ids)));
    } catch (_error) {
      // localStorage can fail in private modes; the in-memory state still works for the session.
    }
  }

  function normalizeSnapshot(gid, product = {}) {
    const id = normalizeProductGid(product.id || gid);
    if (!id) return null;

    const title = cleanText(product.title) || 'Producto';
    const handle = cleanText(product.handle);
    let url = cleanText(product.url);
    if (!url && handle) url = `/products/${handle}`;

    return {
      id,
      title,
      handle,
      url: url || '#',
      image: cleanText(product.image),
      imageAlt: cleanText(product.imageAlt) || title,
      amount: cleanText(product.amount),
      currency: cleanText(product.currency) || 'MXN',
      priceText: cleanText(product.priceText),
      variantId: cleanText(product.variantId),
      variants: normalizeProductVariants(product.variants, product),
      available: product.available !== false,
      source: cleanText(product.source) || 'local',
    };
  }

  function moneyTextToCents(value) {
    const normalized = String(value || '').replace(/[^\d.,-]/g, '').replace(/,/g, '');
    const amount = Number(normalized);
    return Number.isFinite(amount) ? Math.round(amount * 100) : 0;
  }

  function amountToCents(value) {
    const amount = Number(value || 0);
    return Number.isFinite(amount) ? Math.round(amount * 100) : 0;
  }

  function normalizeProductVariants(variants, product = {}) {
    const list = Array.isArray(variants) ? variants : [];
    const normalized = list
      .map((variant) => {
        if (!variant || typeof variant !== 'object') return null;
        const id = cleanText(variantIdFromGid(variant.id || variant.variantId));
        if (!id) return null;
        const price = Number(variant.price || 0) || amountToCents(variant.amount) || moneyTextToCents(product.priceText);
        return {
          id,
          title: cleanText(variant.title) || 'Opción única',
          available: variant.available !== false,
          price,
          compare_at_price: Number(variant.compare_at_price || 0) || amountToCents(variant.compareAtAmount),
        };
      })
      .filter(Boolean);

    if (normalized.length > 0) return normalized;

    const fallbackId = cleanText(variantIdFromGid(product.variantId));
    if (!fallbackId) return [];
    return [
      {
        id: fallbackId,
        title: cleanText(product.variantTitle) || 'Opción única',
        available: product.available !== false,
        price: amountToCents(product.amount) || moneyTextToCents(product.priceText),
        compare_at_price: 0,
      },
    ];
  }

  function readLocalSnapshots() {
    try {
      const parsed = JSON.parse(window.localStorage.getItem(SNAPSHOT_KEY) || '{}');
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return new Map();

      const snapshots = new Map();
      Object.entries(parsed).forEach(([rawId, product]) => {
        const snapshot = normalizeSnapshot(rawId, product);
        if (snapshot) snapshots.set(snapshot.id, snapshot);
      });
      return snapshots;
    } catch (_error) {
      return new Map();
    }
  }

  function writeLocalSnapshots() {
    try {
      const payload = {};
      state.snapshots.forEach((product, gid) => {
        payload[gid] = product;
      });
      window.localStorage.setItem(SNAPSHOT_KEY, JSON.stringify(payload));
    } catch (_error) {
      // Product snapshots are a display helper. Wishlist IDs remain the source of truth.
    }
  }

  function saveSnapshot(gid, product) {
    const snapshot = normalizeSnapshot(gid, product);
    if (!snapshot) return;
    state.snapshots.set(snapshot.id, snapshot);
    state.productCache.set(snapshot.id, snapshot);
    writeLocalSnapshots();
  }

  function removeSnapshot(gid) {
    const normalized = normalizeProductGid(gid);
    if (!normalized) return;
    state.snapshots.delete(normalized);
    state.productCache.delete(normalized);
    writeLocalSnapshots();
  }

  function firstText(root, selectors) {
    if (!root) return '';
    for (const selector of selectors) {
      const element = root.querySelector?.(selector);
      const text = cleanText(element?.textContent);
      if (text) return text;
    }
    return '';
  }

  function firstImage(root) {
    const element =
      root?.querySelector?.('img[src], img[data-src], img[data-lazy-src]') ||
      document.querySelector('meta[property="og:image"]');
    if (!element) return '';
    return cleanText(element.getAttribute('src') || element.getAttribute('data-src') || element.getAttribute('data-lazy-src') || element.getAttribute('content'));
  }

  function firstVariantId(root) {
    const datasetVariant =
      root?.querySelector?.('[data-variant-id]:not([data-corgi-wishlist-add-cart])')?.dataset?.variantId ||
      document.querySelector('product-form-component input[name="id"], form[action*="/cart/add"] input[name="id"], input[name="id"]')?.value ||
      '';
    return cleanText(datasetVariant);
  }

  function extractSnapshot(button, gid) {
    const root =
      button.closest?.('[data-product-url], .cfh2-search-product, .corgi-product-card, .cart-items__table-row, sticky-add-to-cart, product-form-component, .product-information, .shopify-section') ||
      document;
    const handle = cleanText(button.dataset.productHandle || root.dataset?.productHandle);
    const url = cleanText(button.dataset.productUrl || root.dataset?.productUrl || (handle ? `/products/${handle}` : window.location.pathname));
    const title =
      cleanText(button.dataset.productTitle || root.dataset?.productTitle) ||
      firstText(root, [
        '.cfh2-search-product__title',
        '.cart-items__title',
        '[data-testid="sticky-product-title"]',
        '.product-title',
        'h1',
      ]) ||
      firstText(document, ['h1']);
    const priceText =
      cleanText(button.dataset.productPrice || root.dataset?.productPrice) ||
      firstText(root, [
        '.cfh2-search-product__price',
        '.cart-items__price',
        '[data-testid="sticky-price-display"]',
        '.price',
      ]);
    const image = cleanText(button.dataset.productImage || root.dataset?.productImage) || firstImage(root);
    const variantId = cleanText(button.dataset.variantId || root.dataset?.variantId) || firstVariantId(root);

    return {
      id: gid,
      title,
      handle,
      url,
      image,
      imageAlt: title,
      priceText,
      variantId,
      available: true,
      source: 'local',
    };
  }

  function getButtonProductGid(button) {
    if (!(button instanceof HTMLElement)) return '';
    return (
      normalizeProductGid(button.dataset.wishlistProductGid) ||
      normalizeProductGid(button.dataset.productGid) ||
      normalizeProductGid(button.dataset.productId)
    );
  }

  function setButtonState(button, isSaved) {
    if (!(button instanceof HTMLElement)) return;
    button.classList.toggle('is-wishlisted', isSaved);
    button.setAttribute('aria-pressed', isSaved ? 'true' : 'false');
    button.setAttribute('aria-label', isSaved ? label('remove') : label('add'));
    button.title = isSaved ? label('remove') : label('add');
  }

  function updateButtons() {
    const saved = new Set(state.ids);
    document.querySelectorAll('[data-corgi-wishlist-button]').forEach((button) => {
      setButtonState(button, saved.has(getButtonProductGid(button)));
    });
  }

  function dispatchChange() {
    updateButtons();
    renderSections();
    document.dispatchEvent(
      new CustomEvent('corgi:wishlist:change', {
        bubbles: true,
        detail: { wishlist: state.ids.slice() },
      })
    );
  }

  function safeStorageGet(storage, key) {
    try {
      return storage.getItem(key);
    } catch (_error) {
      return '';
    }
  }

  function safeStorageSet(storage, key, value) {
    try {
      storage.setItem(key, value);
    } catch (_error) {
      // Storage can fail in private modes; the in-memory state still works for the session.
    }
  }

  function safeStorageRemove(storage, key) {
    try {
      storage.removeItem(key);
    } catch (_error) {
      // Ignore storage cleanup failures.
    }
  }

  function hasCustomerAccountClient() {
    return Boolean(String(CONFIG.customerAccountClientId || '').trim());
  }

  function getStoredCustomerAuth() {
    try {
      const parsed = JSON.parse(safeStorageGet(window.localStorage, CUSTOMER_ACCOUNT_AUTH_KEY) || '{}');
      if (!parsed || typeof parsed !== 'object') return null;
      if (!parsed.accessToken && !parsed.refreshToken) return null;
      return parsed;
    } catch (_error) {
      return null;
    }
  }

  function storeCustomerAuth(data = {}) {
    const accessToken = cleanText(data.access_token || data.accessToken);
    if (!accessToken) return false;

    const expiresIn = Number(data.expires_in || data.expiresIn || 0);
    const expiresAt = Number(data.expiresAt || 0) || Date.now() + Math.max(expiresIn - 60, 60) * 1000;
    const payload = {
      accessToken,
      refreshToken: cleanText(data.refresh_token || data.refreshToken || state.customerAccountRefreshToken),
      idToken: cleanText(data.id_token || data.idToken),
      expiresAt,
    };

    state.customerAccountToken = payload.accessToken;
    state.customerAccountRefreshToken = payload.refreshToken;
    state.customerAccountExpiresAt = payload.expiresAt;
    safeStorageSet(window.localStorage, CUSTOMER_ACCOUNT_AUTH_KEY, JSON.stringify(payload));
    return true;
  }

  function clearCustomerAuth() {
    state.customerAccountToken = '';
    state.customerAccountRefreshToken = '';
    state.customerAccountExpiresAt = 0;
    state.customerId = '';
    safeStorageRemove(window.localStorage, CUSTOMER_ACCOUNT_AUTH_KEY);
  }

  function loadStoredCustomerAuth() {
    const auth = getStoredCustomerAuth();
    if (!auth) return false;
    state.customerAccountToken = auth.accessToken;
    state.customerAccountRefreshToken = auth.refreshToken || '';
    state.customerAccountExpiresAt = Number(auth.expiresAt || 0);
    return true;
  }

  function getCustomerAccountRedirectUri() {
    const configured = cleanText(CONFIG.customerAccountRedirectUri || CONFIG.customerAccountRedirectPath);
    if (configured && /^https?:\/\//i.test(configured)) return configured;
    if (configured && configured.startsWith('/')) return `${window.location.origin}${configured}`;
    return `${window.location.origin}${window.location.pathname}`;
  }

  function base64UrlEncode(input) {
    const bytes = input instanceof Uint8Array ? input : new TextEncoder().encode(String(input || ''));
    let binary = '';
    bytes.forEach((byte) => {
      binary += String.fromCharCode(byte);
    });
    return window.btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
  }

  function randomUrlToken(length = 32) {
    const bytes = new Uint8Array(length);
    window.crypto.getRandomValues(bytes);
    return base64UrlEncode(bytes);
  }

  async function generateCodeChallenge(verifier) {
    const digest = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
    return base64UrlEncode(new Uint8Array(digest));
  }

  async function discoverCustomerAuthConfig() {
    const response = await fetch('/.well-known/openid-configuration', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error('Customer Account auth discovery failed');
    return response.json();
  }

  async function discoverCustomerApiEndpoint() {
    if (state.customerAccountEndpoint) return state.customerAccountEndpoint;
    if (CONFIG.customerAccountEndpoint) {
      state.customerAccountEndpoint = String(CONFIG.customerAccountEndpoint);
      return state.customerAccountEndpoint;
    }

    const response = await fetch('/.well-known/customer-account-api', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error('Customer Account API discovery failed');
    const data = await response.json();
    state.customerAccountEndpoint = data.graphql_api || '/customer/api/2026-04/graphql';
    return state.customerAccountEndpoint;
  }

  function parseCustomerAccountCallback() {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code') || '';
    const stateParam = params.get('state') || '';
    const storedRaw = safeStorageGet(window.sessionStorage, CUSTOMER_ACCOUNT_PKCE_KEY);
    if (!code || !stateParam || !storedRaw) return null;

    try {
      const stored = JSON.parse(storedRaw);
      if (!stored || stored.state !== stateParam || !stored.verifier) return null;
      return { code, stored };
    } catch (_error) {
      return null;
    }
  }

  function cleanCustomerAccountCallbackUrl() {
    const url = new URL(window.location.href);
    ['code', 'state', 'error', 'error_description'].forEach((key) => url.searchParams.delete(key));
    window.history.replaceState({}, '', url.toString());
  }

  async function exchangeCustomerAccountCode(callback) {
    const authConfig = await discoverCustomerAuthConfig();
    const body = new URLSearchParams();
    body.append('grant_type', 'authorization_code');
    body.append('client_id', cleanText(CONFIG.customerAccountClientId));
    body.append('redirect_uri', callback.stored.redirectUri);
    body.append('code', callback.code);
    body.append('code_verifier', callback.stored.verifier);

    const response = await fetch(authConfig.token_endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body,
    });
    const data = await response.json();
    if (!response.ok || !data.access_token) throw new Error(data.error_description || data.error || 'Customer Account token exchange failed');

    storeCustomerAuth(data);
    state.customerAccountReturnTo = cleanText(callback.stored.returnTo);
    safeStorageRemove(window.sessionStorage, CUSTOMER_ACCOUNT_PKCE_KEY);
    cleanCustomerAccountCallbackUrl();
    return true;
  }

  async function refreshCustomerAuth() {
    if (!state.customerAccountRefreshToken || !hasCustomerAccountClient()) return false;
    try {
      const authConfig = await discoverCustomerAuthConfig();
      const body = new URLSearchParams();
      body.append('grant_type', 'refresh_token');
      body.append('client_id', cleanText(CONFIG.customerAccountClientId));
      body.append('refresh_token', state.customerAccountRefreshToken);

      const response = await fetch(authConfig.token_endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body,
      });
      const data = await response.json();
      if (!response.ok || !data.access_token) throw new Error(data.error || 'Customer Account refresh failed');
      storeCustomerAuth(data);
      return true;
    } catch (_error) {
      clearCustomerAuth();
      return false;
    }
  }

  function canAttemptSilentCustomerAuth() {
    if (!hasCustomerAccountClient() || CONFIG.designMode) return false;
    const lastAttempt = Number(safeStorageGet(window.sessionStorage, CUSTOMER_ACCOUNT_SILENT_KEY) || 0);
    return Date.now() - lastAttempt > 10 * 60 * 1000;
  }

  async function startCustomerAccountAuth({ silent = false } = {}) {
    if (!hasCustomerAccountClient()) return false;
    const authConfig = await discoverCustomerAuthConfig();
    const verifier = randomUrlToken(48);
    const challenge = await generateCodeChallenge(verifier);
    const stateParam = randomUrlToken(24);
    const nonce = randomUrlToken(24);
    const redirectUri = getCustomerAccountRedirectUri();

    safeStorageSet(
      window.sessionStorage,
      CUSTOMER_ACCOUNT_PKCE_KEY,
      JSON.stringify({
        verifier,
        state: stateParam,
        nonce,
        redirectUri,
        returnTo: window.location.href,
        createdAt: Date.now(),
      })
    );
    safeStorageSet(window.sessionStorage, CUSTOMER_ACCOUNT_SILENT_KEY, String(Date.now()));

    const url = new URL(authConfig.authorization_endpoint);
    url.searchParams.set('scope', 'openid email customer-account-api:full');
    url.searchParams.set('client_id', cleanText(CONFIG.customerAccountClientId));
    url.searchParams.set('response_type', 'code');
    url.searchParams.set('redirect_uri', redirectUri);
    url.searchParams.set('state', stateParam);
    url.searchParams.set('nonce', nonce);
    url.searchParams.set('code_challenge', challenge);
    url.searchParams.set('code_challenge_method', 'S256');
    if (silent) url.searchParams.set('prompt', 'none');

    window.location.assign(url.toString());
    return true;
  }

  async function ensureCustomerAccountToken() {
    const callback = parseCustomerAccountCallback();
    if (callback) {
      try {
        return await exchangeCustomerAccountCode(callback);
      } catch (_error) {
        safeStorageRemove(window.sessionStorage, CUSTOMER_ACCOUNT_PKCE_KEY);
        cleanCustomerAccountCallbackUrl();
        clearCustomerAuth();
        return false;
      }
    }

    const params = new URLSearchParams(window.location.search);
    if (params.get('error')) {
      cleanCustomerAccountCallbackUrl();
      return false;
    }

    if (loadStoredCustomerAuth()) {
      if (state.customerAccountExpiresAt > Date.now() + 60000) return true;
      return refreshCustomerAuth();
    }

    if (CONFIG.customerLoggedIn && canAttemptSilentCustomerAuth()) {
      await startCustomerAccountAuth({ silent: true });
    }

    return false;
  }

  async function customerAccountQuery(query, variables = {}) {
    if (!state.customerAccountToken) throw new Error('Customer Account token missing');
    const endpoint = await discoverCustomerApiEndpoint();
    const response = await fetch(endpoint, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Authorization: state.customerAccountToken,
      },
      body: JSON.stringify({ query, variables }),
    });
    const json = await response.json();
    if (!response.ok || json.errors) {
      throw new Error(json.errors?.[0]?.message || 'Customer Account query failed');
    }
    return json.data;
  }

  function parseWishlistValue(value) {
    try {
      const parsed = JSON.parse(value || '[]');
      return Array.isArray(parsed) ? dedupe(parsed) : [];
    } catch (_error) {
      return [];
    }
  }

  async function readCustomerWishlist() {
    const data = await customerAccountQuery(`
      query CorgiWishlistCustomer {
        customer {
          id
          metafield(namespace: "${WISHLIST_NAMESPACE}", key: "${WISHLIST_KEY}") {
            value
          }
        }
      }
    `);
    state.customerId = data.customer?.id || '';
    state.serverLoggedIn = Boolean(state.customerId);
    return parseWishlistValue(data.customer?.metafield?.value);
  }

  async function loadServerWishlist() {
    try {
      const hasToken = await ensureCustomerAccountToken();
      state.serverLoaded = true;
      if (!hasToken) {
        state.serverLoggedIn = false;
        return;
      }

      const serverIds = await readCustomerWishlist();
      const merged = dedupe([...serverIds, ...state.ids]);
      state.ids = merged;
      writeLocal(merged);
      if (merged.length !== serverIds.length || merged.some((id, index) => id !== serverIds[index])) {
        await saveServerWishlist(merged);
      }
      dispatchChange();
      if (state.customerAccountReturnTo) {
        const returnUrl = new URL(state.customerAccountReturnTo, window.location.origin);
        if (returnUrl.origin === window.location.origin && returnUrl.href !== window.location.href) {
          window.location.replace(returnUrl.href);
        }
      }
    } catch (_error) {
      state.serverLoaded = true;
      state.serverLoggedIn = false;
      clearCustomerAuth();
    }
  }

  async function saveServerWishlist(ids = state.ids) {
    const wishlist = dedupe(ids);
    if (!state.customerAccountToken || !state.customerId) return false;
    state.syncing = true;
    document.documentElement.classList.add('corgi-wishlist-syncing');
    try {
      const data = await customerAccountQuery(
        `mutation CorgiWishlistSet($metafields: [MetafieldsSetInput!]!) {
          metafieldsSet(metafields: $metafields) {
            metafields {
              key
              namespace
              value
            }
            userErrors {
              field
              message
              code
            }
          }
        }`,
        {
          metafields: [
            {
              ownerId: state.customerId,
              namespace: WISHLIST_NAMESPACE,
              key: WISHLIST_KEY,
              type: 'json',
              value: JSON.stringify(wishlist),
            },
          ],
        }
      );
      const errors = data.metafieldsSet?.userErrors || [];
      if (errors.length > 0) throw new Error(errors[0].message || 'Wishlist metafield write failed');
      state.serverLoggedIn = true;
      return true;
    } catch (_error) {
      return false;
    } finally {
      state.syncing = false;
      document.documentElement.classList.remove('corgi-wishlist-syncing');
    }
  }

  async function toggleWishlist(button) {
    const gid = getButtonProductGid(button);
    if (!gid) return;

    const isSaved = state.ids.includes(gid);
    if (isSaved) {
      removeSnapshot(gid);
    } else {
      saveSnapshot(gid, extractSnapshot(button, gid));
    }

    const next = isSaved ? state.ids.filter((id) => id !== gid) : dedupe([gid, ...state.ids]);
    state.ids = next;
    writeLocal(next);
    dispatchChange();
    window.CorgiAnalytics?.track?.(isSaved ? 'wishlist_remove' : 'wishlist_add', {
      product_gid: gid,
      source: button?.dataset?.wishlistSource || button?.dataset?.source || 'wishlist_button',
    });

    if (button instanceof HTMLElement) button.classList.add('is-saving');
    await saveServerWishlist(next);
    if (button instanceof HTMLElement) button.classList.remove('is-saving');
  }

  async function storefrontQuery(query, variables) {
    const token = String(CONFIG.storefrontToken || '').trim();
    if (!token) throw new Error('Storefront token missing');

    const response = await fetch(CONFIG.storefrontEndpoint || '/api/2026-04/graphql.json', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': token,
      },
      body: JSON.stringify({ query, variables }),
    });

    const json = await response.json();
    if (!response.ok || json.errors) {
      throw new Error(json.errors?.[0]?.message || 'Storefront query failed');
    }
    return json.data;
  }

  async function fetchProducts(ids) {
    const missing = ids.filter((id) => !state.productCache.has(id));
    const hasStorefrontToken = Boolean(String(CONFIG.storefrontToken || '').trim());

    if (missing.length > 0 && hasStorefrontToken) {
      const data = await storefrontQuery(
        `query GetWishlistProducts($ids: [ID!]!) {
          nodes(ids: $ids) {
            ... on Product {
              id
              title
              handle
              featuredImage {
                url
                altText
              }
              priceRange {
                minVariantPrice {
                  amount
                  currencyCode
                }
              }
              variants(first: 1) {
              variants(first: 100) {
                edges {
                  node {
                    id
                    title
                    availableForSale
                    price {
                      amount
                      currencyCode
                    }
                    compareAtPrice {
                      amount
                      currencyCode
                    }
                  }
                }
              }
            }
          }
        }`,
        { ids: missing }
      );

      (data.nodes || []).forEach((node) => {
        if (!node || !node.id) return;
        const variants = (node.variants?.edges || [])
          .map((edge) => edge?.node)
          .filter(Boolean)
          .map((variant) => ({
            id: variantIdFromGid(variant.id),
            title: variant.title || 'Opción única',
            available: Boolean(variant.availableForSale),
            price: amountToCents(variant.price?.amount),
            compare_at_price: amountToCents(variant.compareAtPrice?.amount),
          }));
        const firstVariant = variants.find((variant) => variant.available) || variants[0] || null;
        const firstPrice = firstVariant?.price ? firstVariant.price / 100 : node.priceRange?.minVariantPrice?.amount || '';
        const product = {
          id: node.id,
          title: node.title || '',
          handle: node.handle || '',
          url: node.handle ? `/products/${node.handle}` : '#',
          image: node.featuredImage?.url || '',
          imageAlt: node.featuredImage?.altText || node.title || '',
          amount: firstPrice,
          currency: node.priceRange?.minVariantPrice?.currencyCode || 'MXN',
          variantId: firstVariant?.id || '',
          variants,
          available: variants.some((variant) => variant.available),
        };
        state.productCache.set(node.id, product);
        state.snapshots.set(node.id, product);
      });
      writeLocalSnapshots();
    }

    return ids.map((id) => state.productCache.get(id) || state.snapshots.get(id)).filter(Boolean);
  }

  function formatPrice(product) {
    if (product.priceText) return product.priceText;
    if (product.amount === '' || product.amount == null) return '';
    const amount = Number(product.amount || 0);
    const currency = product.currency || 'MXN';
    if (!Number.isFinite(amount)) return '';
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency,
    }).format(amount);
  }

  function escapeHtml(value) {
    const span = document.createElement('span');
    span.textContent = String(value || '');
    return span.innerHTML;
  }

  function escapeScriptJson(value) {
    return String(value || '')
      .replace(/</g, '\\u003c')
      .replace(/>/g, '\\u003e')
      .replace(/&/g, '\\u0026')
      .replace(/\u2028/g, '\\u2028')
      .replace(/\u2029/g, '\\u2029');
  }

  function getQuickAddPayload(product) {
    const variants = normalizeProductVariants(product.variants, product);
    const price = variants.find((variant) => variant.available)?.price || variants[0]?.price || amountToCents(product.amount) || moneyTextToCents(product.priceText);
    const compareAt = variants.find((variant) => variant.compare_at_price > price)?.compare_at_price || 0;
    const discount = compareAt > price ? Math.round(((compareAt - price) * 100) / compareAt) : 0;

    return {
      product_id: productIdFromGid(product.id),
      handle: product.handle || '',
      url: product.url || '#',
      title: product.title || 'Producto',
      image: product.image || '',
      rating_text: '',
      brand_label: '',
      description_label: '',
      type_label: '',
      metadata_label: '',
      detail_lines: [],
      price,
      compare_at_price: compareAt,
      discount_percentage: discount,
      variants,
    };
  }

  function renderLoading(section) {
    section.innerHTML = `
      <div class="corgi-wishlist__state">
        <span class="corgi-wishlist__spinner" aria-hidden="true"></span>
        <span>Cargando lista de deseos...</span>
      </div>
    `;
  }

  function renderEmpty(section) {
    section.innerHTML = `
      <div class="corgi-wishlist__empty">
        <h2>${escapeHtml(label('empty'))}</h2>
        ${state.serverLoggedIn ? '' : `<p>${escapeHtml(label('loginNote'))}</p>`}
      </div>
    `;
  }

  function renderError(section, message) {
    section.innerHTML = `
      <div class="corgi-wishlist__empty corgi-wishlist__empty--error">
        <h2>${escapeHtml(message || label('loadError'))}</h2>
        ${state.serverLoggedIn ? '' : `<p>${escapeHtml(label('loginNote'))}</p>`}
      </div>
    `;
  }

  function renderProductGrid(section, products) {
    section.innerHTML = `
      <div class="corgi-wishlist__grid">
        ${products
          .map((product) => {
            const payload = getQuickAddPayload(product);
            const canQuickAdd = payload.variants.length > 0;
            return `
              <article class="corgi-wishlist-card" data-wishlist-product-gid="${escapeHtml(product.id)}" data-corgi-mobile-quick-card>
                <a class="corgi-wishlist-card__media" href="${escapeHtml(product.url)}">
                  ${
                    product.image
                      ? `<img src="${escapeHtml(product.image)}" alt="${escapeHtml(product.imageAlt)}" loading="lazy">`
                      : `<span class="corgi-wishlist-card__placeholder" aria-hidden="true"></span>`
                  }
                </a>
                <div class="corgi-wishlist-card__body">
                  <a class="corgi-wishlist-card__title" href="${escapeHtml(product.url)}">${escapeHtml(product.title)}</a>
                  <span class="corgi-wishlist-card__price">${escapeHtml(formatPrice(product))}</span>
                  <div class="corgi-wishlist-card__actions">
                    <button
                      type="button"
                      class="corgi-wishlist-card__add"
                      data-corgi-mobile-quick-trigger
                      data-corgi-mobile-quick-allow-desktop
                      ${canQuickAdd ? '' : 'disabled'}
                    >
                      ${escapeHtml(label('addToCart'))}
                    </button>
                    <button
                      type="button"
                      class="corgi-wishlist-card__remove"
                      data-corgi-wishlist-remove
                      data-wishlist-product-gid="${escapeHtml(product.id)}"
                    >
                      ${escapeHtml(label('removeItem'))}
                    </button>
                  </div>
                </div>
                <script type="application/json" data-corgi-mobile-quick-add-payload>${escapeScriptJson(JSON.stringify(payload))}</script>
              </article>
            `;
          })
          .join('')}
      </div>
    `;
  }

  async function renderSection(section) {
    if (!(section instanceof HTMLElement)) return;

    if (!state.ids.length) {
      renderEmpty(section);
      return;
    }

    renderLoading(section);
    try {
      const products = await fetchProducts(state.ids);
      if (!products.length) {
        if (!CONFIG.storefrontToken) {
          renderError(section, CONFIG.designMode ? label('tokenMissing') : label('loadError'));
        } else {
          renderEmpty(section);
        }
        return;
      }
      renderProductGrid(section, products);
    } catch (_error) {
      const localProducts = state.ids.map((id) => state.productCache.get(id) || state.snapshots.get(id)).filter(Boolean);
      if (localProducts.length) {
        renderProductGrid(section, localProducts);
        return;
      }
      renderError(section, CONFIG.designMode ? label('tokenMissing') : label('loadError'));
    }
  }

  function renderSections() {
    document.querySelectorAll('[data-corgi-wishlist-section]').forEach((section) => {
      renderSection(section);
    });
  }

  function getCartSectionIds() {
    const sectionIds = new Set();
    document.querySelectorAll('cart-items-component').forEach((component) => {
      if (component instanceof HTMLElement && component.dataset.sectionId) {
        sectionIds.add(component.dataset.sectionId);
      }
    });
    document.querySelectorAll('[data-corgi-cart-render-section-id]').forEach((sectionRoot) => {
      if (sectionRoot instanceof HTMLElement && sectionRoot.dataset.corgiCartRenderSectionId) {
        sectionIds.add(sectionRoot.dataset.corgiCartRenderSectionId);
      }
    });
    return Array.from(sectionIds);
  }

  function openCartDrawer() {
    const cartDrawer = document.querySelector('cart-drawer-component');
    if (cartDrawer && typeof cartDrawer.open === 'function') {
      window.requestAnimationFrame(() => cartDrawer.open());
      return;
    }
    const cartTrigger = document.querySelector('[data-testid="cart-drawer-trigger"]');
    if (cartTrigger instanceof HTMLElement) cartTrigger.click();
  }

  async function addVariantToCart(button) {
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;
    const variantId = String(button.dataset.variantId || '').trim();
    if (!variantId) return;

    const originalText = button.textContent;
    button.disabled = true;
    button.textContent = 'Agregando...';

    try {
      const formData = new FormData();
      const sections = getCartSectionIds();
      formData.append('id', variantId);
      formData.append('quantity', '1');
      if (sections.length > 0) {
        formData.append('sections', sections.join(','));
        formData.append('sections_url', window.location.pathname);
      }

      const response = await fetch(CONFIG.routes?.cartAddUrl || '/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        body: formData,
      });
      const data = await response.json();
      if (!response.ok || data.status) throw new Error(data.description || data.message || 'Cart add failed');

      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: data,
            sourceId: variantId,
            data: {
              source: 'corgi-wishlist',
              itemCount: 1,
              variantId,
              sections: data.sections || {},
            },
          },
        })
      );
      openCartDrawer();
      button.textContent = 'Agregado';
    } catch (_error) {
      button.textContent = 'Intenta otra vez';
    } finally {
      window.setTimeout(() => {
        button.disabled = false;
        button.textContent = originalText || label('addToCart');
      }, 1200);
    }
  }

  function handleDocumentClick(event) {
    const wishlistButton = event.target.closest?.('[data-corgi-wishlist-button]');
    if (wishlistButton) {
      event.preventDefault();
      event.stopPropagation();
      toggleWishlist(wishlistButton);
      return;
    }

    const removeButton = event.target.closest?.('[data-corgi-wishlist-remove]');
    if (removeButton) {
      event.preventDefault();
      const gid = getButtonProductGid(removeButton);
      if (!gid) return;
      state.ids = state.ids.filter((id) => id !== gid);
      removeSnapshot(gid);
      writeLocal(state.ids);
      dispatchChange();
      saveServerWishlist(state.ids);
      return;
    }

    const addCartButton = event.target.closest?.('[data-corgi-wishlist-add-cart]');
    if (addCartButton) {
      event.preventDefault();
      addVariantToCart(addCartButton);
    }
  }

  function init() {
    state.ids = readLocal();
    state.snapshots = readLocalSnapshots();
    state.snapshots.forEach((product, gid) => state.productCache.set(gid, product));
    state.ready = true;
    updateButtons();
    renderSections();

    document.addEventListener('click', handleDocumentClick);

    const observer = new MutationObserver(() => updateButtons());
    observer.observe(document.documentElement, { childList: true, subtree: true });

    loadServerWishlist().finally(() => {
      updateButtons();
      renderSections();
    });
  }

  window.CorgiWishlist = {
    getIds: () => state.ids.slice(),
    add: (id, product) => {
      const gid = normalizeProductGid(id);
      if (!gid) return Promise.resolve(false);
      if (product) saveSnapshot(gid, product);
      state.ids = dedupe([gid, ...state.ids]);
      writeLocal(state.ids);
      dispatchChange();
      return saveServerWishlist(state.ids);
    },
    remove: (id) => {
      const gid = normalizeProductGid(id);
      state.ids = state.ids.filter((saved) => saved !== gid);
      removeSnapshot(gid);
      writeLocal(state.ids);
      dispatchChange();
      return saveServerWishlist(state.ids);
    },
    refresh: () => {
      updateButtons();
      renderSections();
    },
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
